import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
} from '@mui/material';
import { FieldLabel, formFieldSx } from '../../../styles/formStyles';
import { createEditCancelButtonSx, createEditPrimaryButtonSx } from '../../../styles/pageFormStyles';
import { duplicateVersion, isValidVersion, nextPatchVersion } from './questionnaire.service';

/**
 * Creates the next catalogue version as a copy of an existing one.
 *
 * The copy is the point: a published version's questions must stay exactly as they were when
 * assessments were scored against them, so an "upgrade" forks rather than edits.
 */
export default function DuplicateVersionModal({ open, baseVersion, existingVersions = [], onClose, onCreated }) {
    const [version, setVersion] = useState('');
    const [changeNote, setChangeNote] = useState('');
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        setVersion(nextPatchVersion(existingVersions));
        setChangeNote('');
        setError('');
        setSaving(false);
        // existingVersions is a fresh array each render, so keying this on `open` alone keeps the
        // suggested version from being recomputed under the admin while they are typing over it.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const handleCreate = async () => {
        setSaving(true);
        const result = await duplicateVersion({
            baseVersion: baseVersion?.version,
            version,
            changeNote,
        });
        setSaving(false);

        if (!result.success) {
            setError(result.error.message);
            return;
        }
        onCreated(result.data);
    };

    const disabled = saving || !isValidVersion(version);

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: '24px' } }}>
            <DialogTitle sx={{ fontSize: 22, fontWeight: 700, color: '#1C1B1F', pb: 1 }}>
                New questionnaire version
            </DialogTitle>
            <DialogContent>
                <Typography sx={{ color: '#625B71', fontSize: 15, mb: 3 }}>
                    {baseVersion ? (
                        <>
                            Copies <strong>{baseVersion.version}</strong> — {baseVersion.categoryCount ?? 0}{' '}
                            categories, {baseVersion.questionCount ?? 0} questions — into a new draft you can edit.
                            {' '}{baseVersion.version} itself stays untouched.
                        </>
                    ) : (
                        'Select a version to copy.'
                    )}
                </Typography>

                {error ? <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }}>{error}</Alert> : null}

                <Box sx={{ mb: 2.5 }}>
                    <FieldLabel label="Version" required />
                    <TextField
                        fullWidth
                        value={version}
                        onChange={(event) => {
                            setVersion(event.target.value);
                            setError('');
                        }}
                        placeholder="v0.0.2"
                        error={Boolean(version) && !isValidVersion(version)}
                        helperText={
                            version && !isValidVersion(version) ? 'Use three numbers, like v0.0.2.' : ' '
                        }
                        sx={formFieldSx}
                    />
                </Box>

                <Box>
                    <FieldLabel label="Change note" />
                    <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        value={changeNote}
                        onChange={(event) => setChangeNote(event.target.value)}
                        placeholder="What is changing in this version, and why"
                        sx={formFieldSx}
                    />
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3, pt: 1, justifyContent: 'space-between' }}>
                <Button onClick={onClose} sx={createEditCancelButtonSx}>
                    Cancel
                </Button>
                <Button variant="contained" disabled={disabled} onClick={handleCreate} sx={createEditPrimaryButtonSx}>
                    {saving ? 'Creating…' : 'Create draft'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
